import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Users, 
  FileText, 
  Image, 
  BarChart3, 
  LogOut,
  RefreshCw,
  Trash2,
  Eye,
  Mail,
  Phone,
  Calendar,
  Lock,
  Briefcase,
  ExternalLink
} from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColors = {
  new: 'bg-blue-100 text-blue-800',
  contacted: 'bg-yellow-100 text-yellow-800',
  converted: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800',
};

const Admin = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [activeTab, setActiveTab] = useState('dashboard');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/admin/verify`, {}, {
        auth: {
          username: credentials.username,
          password: credentials.password,
        },
      });
      setIsAuthenticated(true);
      toast.success('Login successful');
      fetchData();
    } catch (error) {
      toast.error('Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setCredentials({ username: '', password: '' });
    toast.info('Logged out successfully');
  };

  const fetchData = async () => {
    try {
      const [statsRes, enquiriesRes] = await Promise.all([
        axios.get(`${API}/stats`, { auth: { username: credentials.username, password: credentials.password } }),
        axios.get(`${API}/enquiries`, { auth: { username: credentials.username, password: credentials.password } }),
      ]);
      setStats(statsRes.data);
      setEnquiries(enquiriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch data');
    }
  };

  const updateEnquiryStatus = async (enquiryId, newStatus) => {
    try {
      await axios.patch(
        `${API}/enquiries/${enquiryId}/status?new_status=${newStatus}`,
        {},
        { auth: { username: credentials.username, password: credentials.password } }
      );
      toast.success('Status updated successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const deleteEnquiry = async (enquiryId) => {
    if (!window.confirm('Are you sure you want to delete this enquiry?')) return;
    try {
      await axios.delete(`${API}/enquiries/${enquiryId}`, {
        auth: { username: credentials.username, password: credentials.password },
      });
      toast.success('Enquiry deleted successfully');
      setSelectedEnquiry(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to delete enquiry');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center p-4" data-testid="admin-login-page">
        <Card className="w-full max-w-md border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-[#4ECDC4]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="text-[#4ECDC4]" size={32} />
            </div>
            <CardTitle className="font-heading text-2xl">Admin Login</CardTitle>
            <p className="text-gray-600 text-sm mt-2">SSMCH Administration Panel</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Input
                  type="text"
                  placeholder="Username"
                  value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  className="h-12"
                  required
                  data-testid="admin-username"
                />
              </div>
              <div>
                <Input
                  type="password"
                  placeholder="Password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  className="h-12"
                  required
                  data-testid="admin-password"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-12 bg-[#4ECDC4] hover:bg-[#3DBDB4] text-white rounded-lg font-medium"
                disabled={loading}
                data-testid="admin-login-btn"
              >
                {loading ? 'Logging in...' : 'Login'}
              </Button>
            </form>
            <p className="text-xs text-gray-400 text-center mt-4">
              Default: admin / ssmch2024
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#4ECDC4] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold">S</span>
              </div>
              <div>
                <h1 className="font-heading text-lg font-semibold text-[#1A202C]">SSMCH Admin</h1>
                <p className="text-xs text-gray-500">Administration Panel</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchData}
                className="text-gray-600"
                data-testid="refresh-btn"
              >
                <RefreshCw size={16} className="mr-1" />
                Refresh
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="text-gray-600"
                data-testid="logout-btn"
              >
                <LogOut size={16} className="mr-1" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-gray-200 mb-6">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-[#4ECDC4] data-[state=active]:text-white">
              <BarChart3 size={16} className="mr-2" />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="enquiries" className="data-[state=active]:bg-[#4ECDC4] data-[state=active]:text-white">
              <Users size={16} className="mr-2" />
              Enquiries
            </TabsTrigger>
            <TabsTrigger value="applications" className="data-[state=active]:bg-[#4ECDC4] data-[state=active]:text-white" data-testid="tab-applications">
              <Briefcase size={16} className="mr-2" />
              Job Applications
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Enquiries</p>
                      <p className="text-3xl font-bold text-[#1A202C]">{stats?.total_enquiries || 0}</p>
                    </div>
                    <div className="w-12 h-12 bg-[#4ECDC4]/10 rounded-full flex items-center justify-center">
                      <Users className="text-[#4ECDC4]" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">New Enquiries</p>
                      <p className="text-3xl font-bold text-blue-600">{stats?.new_enquiries || 0}</p>
                    </div>
                    <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center">
                      <Mail className="text-blue-600" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">News Items</p>
                      <p className="text-3xl font-bold text-[#D4A84B]">{stats?.total_news || 0}</p>
                    </div>
                    <div className="w-12 h-12 bg-[#D4A84B]/10 rounded-full flex items-center justify-center">
                      <FileText className="text-[#D4A84B]" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Gallery Images</p>
                      <p className="text-3xl font-bold text-[#9ACD32]">{stats?.total_gallery || 0}</p>
                    </div>
                    <div className="w-12 h-12 bg-[#9ACD32]/10 rounded-full flex items-center justify-center">
                      <Image className="text-[#9ACD32]" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">Total Applications</p>
                      <p className="text-3xl font-bold text-[#1A202C]" data-testid="stat-total-applications">{stats?.total_applications || 0}</p>
                    </div>
                    <div className="w-12 h-12 bg-[#D4A84B]/10 rounded-full flex items-center justify-center">
                      <Briefcase className="text-[#D4A84B]" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card className="border-0 shadow-sm">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">New Applications</p>
                      <p className="text-3xl font-bold text-red-600" data-testid="stat-new-applications">{stats?.new_applications || 0}</p>
                    </div>
                    <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center">
                      <Briefcase className="text-red-600" size={24} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Enquiries */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Recent Enquiries</CardTitle>
              </CardHeader>
              <CardContent>
                {enquiries.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No enquiries yet</p>
                ) : (
                  <div className="space-y-4">
                    {enquiries.slice(0, 5).map((enquiry) => (
                      <div
                        key={enquiry.id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                        onClick={() => setSelectedEnquiry(enquiry)}
                      >
                        <div>
                          <p className="font-medium text-[#1A202C]">{enquiry.name}</p>
                          <p className="text-sm text-gray-600">{enquiry.email}</p>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={statusColors[enquiry.status]}>
                            {enquiry.status}
                          </Badge>
                          <span className="text-xs text-gray-500">
                            {formatDate(enquiry.created_at)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Enquiries Tab */}
          <TabsContent value="enquiries">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="font-heading text-lg">All Enquiries</CardTitle>
              </CardHeader>
              <CardContent>
                {enquiries.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No enquiries yet</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left p-3 text-sm font-semibold text-gray-600">Name</th>
                          <th className="text-left p-3 text-sm font-semibold text-gray-600">Contact</th>
                          <th className="text-left p-3 text-sm font-semibold text-gray-600">Course</th>
                          <th className="text-left p-3 text-sm font-semibold text-gray-600">Source</th>
                          <th className="text-left p-3 text-sm font-semibold text-gray-600">Status</th>
                          <th className="text-left p-3 text-sm font-semibold text-gray-600">Date</th>
                          <th className="text-left p-3 text-sm font-semibold text-gray-600">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {enquiries.map((enquiry) => (
                          <tr key={enquiry.id} className="border-b border-gray-100 hover:bg-gray-50">
                            <td className="p-3">
                              <p className="font-medium text-[#1A202C]">{enquiry.name}</p>
                            </td>
                            <td className="p-3">
                              <p className="text-sm text-gray-600">{enquiry.email}</p>
                              <p className="text-sm text-gray-500">{enquiry.phone}</p>
                            </td>
                            <td className="p-3 text-sm text-gray-600">{enquiry.course}</td>
                            <td className="p-3 text-sm text-gray-600 capitalize">{enquiry.source_page}</td>
                            <td className="p-3">
                              <Select
                                value={enquiry.status}
                                onValueChange={(value) => updateEnquiryStatus(enquiry.id, value)}
                              >
                                <SelectTrigger className="w-32 h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="new">New</SelectItem>
                                  <SelectItem value="contacted">Contacted</SelectItem>
                                  <SelectItem value="converted">Converted</SelectItem>
                                  <SelectItem value="closed">Closed</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="p-3 text-sm text-gray-500">{formatDate(enquiry.created_at)}</td>
                            <td className="p-3">
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setSelectedEnquiry(enquiry)}
                                  data-testid={`view-enquiry-${enquiry.id}`}
                                >
                                  <Eye size={16} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-500 hover:text-red-700"
                                  onClick={() => deleteEnquiry(enquiry.id)}
                                  data-testid={`delete-enquiry-${enquiry.id}`}
                                >
                                  <Trash2 size={16} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Applications Tab — redirects to full CMS */}
          <TabsContent value="applications">
            <Card className="border-0 shadow-sm">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-[#D4A84B]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Briefcase className="text-[#D4A84B]" size={32} />
                </div>
                <h3 className="font-heading text-xl font-semibold text-[#1A202C] mb-2">Job Applications</h3>
                <p className="text-gray-600 mb-1"><span className="font-semibold text-[#1A202C]" data-testid="applications-total">{stats?.total_applications || 0}</span> total applications · <span className="font-semibold text-red-600">{stats?.new_applications || 0}</span> new</p>
                <p className="text-sm text-gray-500 mb-6">The full applicant dashboard (filters, CSV export, document downloads, status management) lives in the new CMS.</p>
                <Link to="/admin/cms/applications">
                  <Button className="bg-[#4ECDC4] hover:bg-[#3DBDB4] text-white rounded-full px-6" data-testid="open-applications-cms">
                    Open Applications Dashboard<ExternalLink size={14} className="ml-2" />
                  </Button>
                </Link>
                <div className="mt-4">
                  <Link to="/admin/cms/careers-config" className="text-sm text-[#4ECDC4] hover:underline">Manage Careers Config →</Link>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Enquiry Detail Dialog */}
      <Dialog open={!!selectedEnquiry} onOpenChange={() => setSelectedEnquiry(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Enquiry Details</DialogTitle>
          </DialogHeader>
          {selectedEnquiry && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-[#4ECDC4]/10 rounded-full flex items-center justify-center">
                  <span className="text-[#4ECDC4] font-bold text-lg">
                    {selectedEnquiry.name.charAt(0)}
                  </span>
                </div>
                <div>
                  <p className="font-semibold text-[#1A202C]">{selectedEnquiry.name}</p>
                  <Badge className={statusColors[selectedEnquiry.status]}>
                    {selectedEnquiry.status}
                  </Badge>
                </div>
              </div>
              <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Mail size={16} className="text-gray-500" />
                  <span className="text-sm">{selectedEnquiry.email}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Phone size={16} className="text-gray-500" />
                  <span className="text-sm">{selectedEnquiry.phone}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar size={16} className="text-gray-500" />
                  <span className="text-sm">{formatDate(selectedEnquiry.created_at)}</span>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Course Interested</p>
                <p className="text-[#1A202C]">{selectedEnquiry.course}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Source Page</p>
                <p className="text-[#1A202C] capitalize">{selectedEnquiry.source_page}</p>
              </div>
              {selectedEnquiry.message && (
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-1">Message</p>
                  <p className="text-[#1A202C] text-sm">{selectedEnquiry.message}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSelectedEnquiry(null)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Admin;
